import React, { useEffect, useState } from "react";
import { ActivityCard, ChoiceButton, MixedText, PlayButton, ProgressPill, useShuffledChoices } from "../shared";

/**
 * PronounFillBlank — "Complete the Sentence" agreement practice.
 *
 * One word (or phrase) of each sentence is blanked out and the rest is the
 * clue. A correct answer reveals the full sentence with audio, highlighting
 * the filled-in part (item.blank_index — word index where the blank starts,
 * 0 for a leading blank, otherwise everything from that word to the end).
 * Completes when every sentence is solved.
 */
function RevealedSentence({ item }) {
  const words = item.full_ar.split(" ");
  const bi = item.blank_index || 0;
  const filled = bi === 0 ? words.slice(0, 1) : words.slice(bi);
  const rest = bi === 0 ? words.slice(1) : words.slice(0, bi);
  const filledSpan = <span style={{ color: "var(--teal)" }}>{filled.join(" ")}</span>;
  return bi === 0 ? (
    <>{filledSpan} {rest.join(" ")}</>
  ) : (
    <>{rest.join(" ")} {filledSpan}</>
  );
}

export default function PronounFillBlank({ data, onComplete, audio }) {
  const [itemIdx, setItemIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [solvedCount, setSolvedCount] = useState(0);

  const items = data.items;
  const item = items[itemIdx];
  const done = solvedCount >= items.length;
  const view = useShuffledChoices(item.choices, item.correct_index, [item]);

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const handlePick = (idx) => {
    if (picked !== null || done) return;
    setPicked(idx);
    if (idx === view.correctIndex) {
      audio.play(item.full_ar);
      setTimeout(() => {
        setSolvedCount((n) => n + 1);
        if (itemIdx + 1 < items.length) {
          setItemIdx((i) => i + 1);
          setPicked(null);
        }
      }, 1600);
    } else {
      // Wrong guess: shake it off and let them try this one again.
      setTimeout(() => setPicked(null), 900);
    }
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-between gap-3 mb-5">
        <ProgressPill>{solvedCount}/{items.length} solved</ProgressPill>
      </div>

      {!done && item && (
        <>
          <div className="text-center mb-2">
            <p dir="rtl" lang="ar" className="font-arabic text-4xl sm:text-5xl leading-[1.6] m-0 mb-1" style={{ color: "var(--parchment)" }}>
              {picked === view.correctIndex ? <RevealedSentence item={item} /> : item.sentence_ar}
            </p>
            <p className="text-sm mt-2 mb-0 inline-flex items-center gap-3" style={{ color: "var(--parchment-dim)" }}>
              {item.sentence_en}
              {picked === view.correctIndex && audio.audioAllowed && (
                <PlayButton text={item.full_ar} playingText={audio.playingText} onPlay={audio.play} />
              )}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 max-w-xl mx-auto mt-5">
            {view.choices.map((choice, idx) => {
              let state = "idle";
              if (picked !== null) {
                if (idx === view.correctIndex && picked === idx) state = "correct";
                else if (idx === picked) state = "wrong";
              }
              return (
                <ChoiceButton key={`${item.id}-${idx}`} arabic state={state} disabled={picked === view.correctIndex} onClick={() => handlePick(idx)}>
                  {choice}
                </ChoiceButton>
              );
            })}
          </div>
        </>
      )}

      {done && (
        <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl" style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}>
          <span className="text-lg leading-none" aria-hidden="true">📝</span>
          <p className="text-sm m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.done_message || `All ${items.length} solved — the word endings told you exactly which pronoun fits.`} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
